import React from 'react';
import { Table, InputNumber, Input, Button, DatePicker, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { F90B1 } from '../../../services/pw9201Api';

interface DetailGridProps {
  details: Partial<F90B1>[];
  isEditing: boolean;
  updateDetailField: (rowIdx: number, field: keyof F90B1, value: F90B1[keyof F90B1]) => void;
  onAdd: () => void;
  onDelete: (rowIdx: number) => void;
}

const dateToInput = (v: string | null | undefined): dayjs.Dayjs | null => {
  if (!v) return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
};

const GENDER_OPTIONS = [
  { value: 0, label: '不限' },
  { value: 1, label: '男' },
  { value: 2, label: '女' },
];

const DetailGrid: React.FC<DetailGridProps> = ({
  details, isEditing,
  updateDetailField, onAdd, onDelete,
}) => {
  const columns: ColumnsType<Partial<F90B1>> = [
    {
      title: '項次',
      key: 'seq',
      width: 50,
      render: (_val, _rec, idx) => idx + 1,
    },
    {
      title: '職稱代碼',
      dataIndex: 'f90b1_11',
      key: 'jobTitle',
      width: 90,
      render: (val: string, _rec, idx) =>
        isEditing ? (
          <Input value={val || ''} maxLength={4} size="small" style={{ width: '100%' }}
            onChange={e => updateDetailField(idx, 'f90b1_11', e.target.value)} />
        ) : val || '',
    },
    {
      title: '職稱名稱',
      dataIndex: 'f90b1_11a',
      key: 'jobTitleName',
      width: 110,
      render: (val: string, _rec, idx) =>
        isEditing ? (
          <Input value={val || ''} maxLength={20} size="small" style={{ width: '100%' }}
            onChange={e => updateDetailField(idx, 'f90b1_11a', e.target.value)} />
        ) : val || '',
    },
    {
      title: '編制人數',
      dataIndex: 'f90b1_12',
      key: 'staffCount',
      width: 80,
      render: (val: number, _rec, idx) =>
        isEditing ? (
          <InputNumber value={val ?? 0} min={0} size="small" style={{ width: '100%' }}
            onChange={v => updateDetailField(idx, 'f90b1_12', v ?? 0)} />
        ) : (val ?? 0),
    },
    {
      title: '現有人數',
      dataIndex: 'f90b1_13',
      key: 'currentCount',
      width: 80,
      render: (val: number, _rec, idx) =>
        isEditing ? (
          <InputNumber value={val ?? 0} min={0} size="small" style={{ width: '100%' }}
            onChange={v => updateDetailField(idx, 'f90b1_13', v ?? 0)} />
        ) : (val ?? 0),
    },
    {
      title: '申請人數',
      dataIndex: 'f90b1_14',
      key: 'reqCount',
      width: 80,
      render: (val: number, _rec, idx) =>
        isEditing ? (
          <InputNumber value={val ?? 0} min={0} size="small" style={{ width: '100%' }}
            onChange={v => updateDetailField(idx, 'f90b1_14', v ?? 0)} />
        ) : (val ?? 0),
    },
    {
      title: '需求日期',
      dataIndex: 'f90b1_15',
      key: 'needDate',
      width: 130,
      render: (val: string | null, _rec, idx) =>
        isEditing ? (
          <DatePicker
            value={dateToInput(val)}
            size="small" style={{ width: '100%' }}
            onChange={(_d, ds) => {
              const dateStr = typeof ds === 'string' ? ds : ds[0];
              updateDetailField(idx, 'f90b1_15', dateStr ? `${dateStr}T00:00:00Z` : null);
            }}
          />
        ) : val ? dayjs(val).format('YYYY-MM-DD') : '',
    },
    {
      title: '增補原因',
      dataIndex: 'f90b1_21',
      key: 'reason',
      width: 150,
      render: (val: string, _rec, idx) =>
        isEditing ? (
          <Input value={val || ''} size="small" style={{ width: '100%' }}
            onChange={e => updateDetailField(idx, 'f90b1_21', e.target.value)} />
        ) : val || '',
    },
    {
      title: '性別',
      dataIndex: 'f90b1_22',
      key: 'gender',
      width: 80,
      render: (val: number, _rec, idx) =>
        isEditing ? (
          <Select value={val ?? 0} size="small" style={{ width: '100%' }}
            options={GENDER_OPTIONS}
            onChange={v => updateDetailField(idx, 'f90b1_22', v)} />
        ) : GENDER_OPTIONS.find(o => o.value === val)?.label || '不限',
    },
    {
      title: '學歷',
      dataIndex: 'f90b1_24',
      key: 'education',
      width: 100,
      render: (val: string, _rec, idx) =>
        isEditing ? (
          <Input value={val || ''} maxLength={20} size="small" style={{ width: '100%' }}
            onChange={e => updateDetailField(idx, 'f90b1_24', e.target.value)} />
        ) : val || '',
    },
    {
      title: '要求條件',
      dataIndex: 'f90b1_25',
      key: 'requirement',
      width: 160,
      render: (val: string, _rec, idx) =>
        isEditing ? (
          <Input value={val || ''} size="small" style={{ width: '100%' }}
            onChange={e => updateDetailField(idx, 'f90b1_25', e.target.value)} />
        ) : val || '',
    },
    {
      title: '科系',
      dataIndex: 'f90b1_27',
      key: 'major',
      width: 120,
      render: (val: string, _rec, idx) =>
        isEditing ? (
          <Input value={val || ''} size="small" style={{ width: '100%' }}
            onChange={e => updateDetailField(idx, 'f90b1_27', e.target.value)} />
        ) : val || '',
    },
    {
      title: '增補理由',
      dataIndex: 'f90b1_26',
      key: 'addReason',
      width: 150,
      render: (val: string, _rec, idx) =>
        isEditing ? (
          <Input value={val || ''} size="small" style={{ width: '100%' }}
            onChange={e => updateDetailField(idx, 'f90b1_26', e.target.value)} />
        ) : val || '',
    },
    {
      title: '部門代號',
      dataIndex: 'f90b1_32',
      key: 'deptCode',
      width: 90,
      render: (val: string, _rec, idx) =>
        isEditing ? (
          <Input value={val || ''} maxLength={8} size="small" style={{ width: '100%' }}
            onChange={e => updateDetailField(idx, 'f90b1_32', e.target.value)} />
        ) : val || '',
    },
    {
      title: '部門名稱',
      dataIndex: 'f90b1_32a',
      key: 'deptName',
      width: 110,
      render: (val: string, _rec, idx) =>
        isEditing ? (
          <Input value={val || ''} size="small" style={{ width: '100%' }}
            onChange={e => updateDetailField(idx, 'f90b1_32a', e.target.value)} />
        ) : val || '',
    },
    ...(isEditing
      ? [
          {
            title: '操作',
            key: 'action',
            width: 60,
            render: (_val: unknown, _rec: Partial<F90B1>, idx: number) => (
              <Button size="small" danger type="link" onClick={() => onDelete(idx)}>
                删除
              </Button>
            ),
          } as ColumnsType<Partial<F90B1>>[number],
        ]
      : []),
  ];

  return (
    <div className="pw9201-detail-grid">
      {isEditing && (
        <div className="pw9201-detail-toolbar">
          <Button size="small" type="dashed" onClick={onAdd}>
            + 明细新增
          </Button>
        </div>
      )}
      <Table<Partial<F90B1>>
        dataSource={details}
        columns={columns}
        rowKey={(_rec, idx) => idx ?? 0}
        size="small"
        pagination={false}
        scroll={{ x: 1800, y: 300 }}
      />
    </div>
  );
};

export default React.memo(DetailGrid);
